import { Skeleton } from '@h2hspark/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useMyRegisteredEvents } from './api';
import {
  TICKETS_AND_ORDERS_HEADING,
  TICKETS_AND_ORDERS_PATH,
  YOUR_REGISTRATIONS_DESCRIPTION,
  YOUR_REGISTRATIONS_HEADING,
} from './constants';
import { RegistrationSummaryPanel } from './registration-summary-panel';

/**
 * **Your registrations** — the web account page's own block (`MEM-022` AC-6).
 *
 * ## AC-6 is a two-surface requirement, and this is the second surface
 *
 * *"The web account page carries a 'Your registrations' block — 'Events you've registered for,
 * with receipts.' — rendering **the same data** as the PWA's Tickets & orders."*
 *
 * **The criterion said "the same order data" until 2026-09-03**, and that was the defect rather
 * than the requirement: this block's own heading promises registrations, and orders are what it
 * got, so a member who attended a free event read a heading about themselves over a list they were
 * not on. Sumit ruled the heading right; AC-3 and AC-6 were reworded to the data the two surfaces
 * now share.
 *
 * The story's Notes are explicit about how the two-surface half decays: *"AC-6 is a two-surface
 * requirement that will drift unless it is one implementation … Two renderers over one source; not
 * two sources."* So this component is deliberately thin. It owns a heading, a sub-line and a link,
 * and **nothing about a row**:
 *
 *   - the data is `useMyRegisteredEvents`, the same hook `TicketsAndOrdersPage` calls, under the same
 *     query key against the same route — so the two surfaces cannot fetch different things, and a
 *     member with both open in one session is served from one cache entry;
 *   - the rows are `RegistrationSummaryPanel`, the same component — so the two cannot *draw*
 *     different things either, which is the half a shared hook alone would not protect.
 *
 * `the-registrations-block-and-the-orders-list-agree.test.tsx` renders both surfaces from one
 * seeded cache entry and asserts the two regions are byte-identical. That is the same shape
 * `one-itemisation-two-screens.test.tsx` uses for `MEM-020` AC-3, and it is what makes AC-6 a
 * property of the code rather than something somebody remembered.
 *
 * ## Why it renders nothing rather than an empty state
 *
 * `TicketsAndOrdersPage` is a screen a member navigated to on purpose, so "you have registered for
 * nothing" is the answer it owes them. This is a block on a page about something else, and an
 * empty card headed "Your registrations" on the account page of a member who has never registered
 * for anything is a section that exists to say nothing. The account page is already long.
 *
 * The link out is always available from the settings row above (`MEM_ACCOUNT_SETTINGS_ROWS`'s
 * `tickets`), which is AC-3's entry point and does not depend on this block being drawn.
 */
/**
 * §2.15's own card treatment for this block: `--surface-card`, a `--border-subtle` hairline,
 * `--radius-xl`, `--shadow-sm` and 26px of padding — the same four cards the account page draws
 * beside it, which is why the class string is the one `my-account-page.tsx` names `ACCOUNT_CARD`
 * plus this card's own padding. It shipped as the generic `Card` primitive with `pt-5`, which is a
 * different corner radius and a different pad from the three cards above it on one screen; the
 * standard's *check the neighbours* is exactly this case.
 */
const REGISTRATIONS_CARD = 'rounded-xl border border-border-subtle bg-card p-6 shadow-sm md:p-[26px]';

export function YourRegistrationsCard(): React.ReactElement | null {
  const registrations = useMyRegisteredEvents({ enabled: true });

  if (registrations.isPending) {
    return (
      <section className={REGISTRATIONS_CARD}>
        <Skeleton className="h-16 w-full rounded-lg" />
      </section>
    );
  }

  const rows = registrations.data?.registrations ?? [];
  if (rows.length === 0) return null;

  return (
    <section className={REGISTRATIONS_CARD}>
      {/* §2.15: the heading at `--text-xl` with 4px under it, the sub-line at `--text-sm` with 18px
          under that, then the rows at a 12px gap. */}
      <h2 className="mb-1 text-xl">{YOUR_REGISTRATIONS_HEADING}</h2>
      <p className="mb-[18px] text-sm text-muted-foreground">{YOUR_REGISTRATIONS_DESCRIPTION}</p>

      <div data-testid="your-registrations">
        <RegistrationSummaryPanel registrations={rows} />
      </div>

      {/*
        Not in §2.15, and kept: this block is capped by what the account page can usefully hold,
        and Tickets & orders is the whole list (`MEM-022` AC-3). The settings row above says the
        same thing, so this is a second door rather than the only one — which is why it is a quiet
        link under the rows rather than a control.
      */}
      <Link
        to={TICKETS_AND_ORDERS_PATH}
        className="mt-4 inline-flex font-brand text-sm font-bold text-brand"
      >
        {TICKETS_AND_ORDERS_HEADING}
      </Link>
    </section>
  );
}
